import { useRef, useState } from "react";
import { getStoredCurrentEnvironment } from "@/lib/environment";
import { configAgentError, forexApi, type ConfigTemplateV1 } from "@/lib/forex-api";

export default function StrategyQualificationPanel({ template, onSaved }: {
  template: ConfigTemplateV1; onSaved: () => void;
}) {
  const [report, setReport] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const requestKey = useRef("");
  const readSequence = useRef(0);
  const scope = report?.scope as { symbol?: string; timeframe?: string; model_id?: string; template_hash?: string } | undefined;
  const stages: Record<string, string> = { engineering: "工程", research: "研究", forward: "前瞻模拟", revoked: "撤销资格" };
  const load = async (file?: File) => {
    const sequence = ++readSequence.current;
    setReport(null); setError(""); setConfirming(false);
    if (!file) return;
    try {
      if (file.size > 8 * 1024 * 1024) throw new Error("报告文件不能超过 8 MB");
      const value = JSON.parse(await file.text());
      if (sequence !== readSequence.current) return;
      if (!value || typeof value !== "object" || !stages[value.stage]) throw new Error("请导入工程、研究、前瞻模拟或资格撤销报告");
      if (value.scope?.template_hash !== template.template_ref.content_hash) throw new Error("报告不属于当前模板版本");
      requestKey.current = crypto.randomUUID(); setReport(value);
    } catch (cause) { if (sequence === readSequence.current) setError(cause instanceof Error ? cause.message : "报告读取失败"); }
  };
  const save = async () => {
    if (!report) return;
    setBusy(true); setError("");
    try {
      await forexApi.recordStrategyQualification(template.template_ref.id, template.template_ref.version, report, requestKey.current);
      setReport(null); setConfirming(false); onSaved();
    } catch (cause) { setError(configAgentError(cause, "报告登记失败，请核对资格证据和审批权限")); }
    finally { setBusy(false); }
  };
  return <details className="mt-3 text-xs">
    <summary className="cursor-pointer">登记验证报告</summary>
    <p className="mt-2 text-text-muted">报告须包含原始验证制品。服务端核对内容摘要并重算研究与前瞻结果，仅有通过结论或统计汇总不能取得资格。</p>
    <input aria-label="验证报告文件" type="file" accept="application/json,.json" disabled={busy}
      onChange={(event) => void load(event.target.files?.[0])} className="mt-2 block" />
    {report && <p className="mt-2">{stages[String(report.stage)]} · {scope?.symbol} · {scope?.timeframe} · {scope?.model_id}</p>}
    {error && <p role="alert" className="mt-2 text-red-600">{error}</p>}
    {report && <button type="button" disabled={busy} onClick={() => setConfirming(true)} className="mt-2 text-brand">核对并登记</button>}
    {confirming && <div role="dialog" aria-modal="true" aria-label="确认登记验证报告" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md space-y-3 rounded-xl bg-white p-6">
        <h3 className="font-semibold">确认登记验证报告</h3>
        <p>{getStoredCurrentEnvironment()?.display_name || "当前环境"} · {template.name} · v{template.template_ref.version} · {stages[String(report?.stage)]}</p>
        <p>请确认已核对原始验证制品、适用范围和风险预算。登记单份报告不代表策略已获得交易资格。撤销会禁止该验证范围的新开仓及加仓，已有持仓仍可退出。</p>
        <button type="button" disabled={busy} onClick={() => setConfirming(false)} className="mr-4">取消</button>
        <button type="button" disabled={busy} onClick={() => void save()} className="text-brand">{busy ? "登记中" : "确认登记"}</button>
      </div>
    </div>}
  </details>;
}
